import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Helmet } from "react-helmet";
import { CheckCircle2, Download, FileUp, ListChecks, Plus, XCircle } from "lucide-react";
import GearPageHeader from "../GearComparison/GearPageHeader";
import {
  fetchMySubmissions,
  fetchSubmissionFields,
  submitGear,
} from "../../api/gearSubmissions";
import {
  downloadCsvTemplate,
  GEAR_PAGE_PATH,
  parseSubmissionCsv,
  validateSubmissionRow,
} from "../../utils/gearSubmission";
import { useMediaQuery } from "react-responsive";
import { getUserIsLoggedIn } from "../../utils";
import { getBorder, getMutedText, HEADING_FONT } from "../../constants/colors";

const BUTTON_SX = {
  borderRadius: "9px",
  fontFamily: HEADING_FONT,
  fontWeight: 600,
  textTransform: "none",
};

const STATUS_COLOR = { pending: "warning", published: "success", rejected: "error" };

// The app theme disables MUI breakpoints, so responsive values use
// react-responsive like the rest of the site.
const useIsMobile = () => useMediaQuery({ maxWidth: 898 });

const getCardSx = (darkMode, isMobile) => ({
  borderRadius: "16px",
  border: `1px solid ${getBorder(darkMode)}`,
  backgroundImage: "none",
  padding: isMobile ? "22px 18px" : "32px 36px",
});

const getFieldGridSx = (columns) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
  columnGap: "20px",
  rowGap: "8px",
});

const SectionLabel = ({ darkMode, children }) => (
  <Typography
    sx={{
      fontFamily: HEADING_FONT,
      fontWeight: 600,
      fontSize: "11px",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: getMutedText(darkMode),
    }}
  >
    {children}
  </Typography>
);

const FieldInput = ({ field, value, onChange, error }) => {
  const common = {
    size: "small",
    fullWidth: true,
    label: `${field.label}${field.required ? " *" : ""}`,
    value: value ?? "",
    onChange: (e) => onChange(field.key, e.target.value),
    error: Boolean(error),
    helperText: error || field.hint || " ",
  };
  if (field.type === "enum") {
    return (
      <TextField select {...common}>
        <MenuItem value="">
          <em>Not specified</em>
        </MenuItem>
        {field.values.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  if (field.type === "text") return <TextField {...common} multiline minRows={3} />;
  return <TextField {...common} inputMode={field.type === "number" ? "decimal" : undefined} />;
};

const SingleItemForm = ({ darkMode, gearType, fields, onSubmitted }) => {
  const isMobile = useIsMobile();
  const [values, setValues] = React.useState({});
  const [note, setNote] = React.useState("");
  const [showErrors, setShowErrors] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [serverError, setServerError] = React.useState("");

  React.useEffect(() => {
    setValues({});
    setShowErrors(false);
    setServerError("");
  }, [gearType]);

  const fieldErrors = React.useMemo(() => {
    const errors = {};
    validateSubmissionRow(fields, values).forEach((message) => {
      const field = fields.find((f) => message.startsWith(f.label));
      errors[field ? field.key : "weight"] = message;
    });
    return errors;
  }, [fields, values]);

  const handleChange = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    setShowErrors(true);
    if (Object.keys(fieldErrors).length) return;
    setSubmitting(true);
    setServerError("");
    const result = await submitGear({ gearType, items: [values], source: "form", note });
    setSubmitting(false);
    if (!result.ok) {
      setServerError(
        result.rowErrors ? result.rowErrors[0].messages.join(", ") : result.error
      );
      return;
    }
    setValues({});
    setNote("");
    setShowErrors(false);
    onSubmitted(result);
  };

  const required = fields.filter((field) => field.required);
  const optional = fields.filter((field) => !field.required);

  return (
    <Stack gap="24px">
      <SectionLabel darkMode={darkMode}>Required</SectionLabel>
      <Box sx={getFieldGridSx(isMobile ? 1 : 2)}>
        {required.map((field) => (
          <FieldInput
            key={field.key}
            field={field}
            value={values[field.key]}
            onChange={handleChange}
            error={showErrors ? fieldErrors[field.key] : ""}
          />
        ))}
      </Box>
      <SectionLabel darkMode={darkMode}>Specs (optional, but the more the better)</SectionLabel>
      <Box sx={getFieldGridSx(isMobile ? 1 : 3)}>
        {optional
          .filter((field) => field.type !== "text")
          .map((field) => (
            <FieldInput
              key={field.key}
              field={field}
              value={values[field.key]}
              onChange={handleChange}
              error={showErrors ? fieldErrors[field.key] : ""}
            />
          ))}
      </Box>
      {optional
        .filter((field) => field.type === "text")
        .map((field) => (
          <FieldInput key={field.key} field={field} value={values[field.key]} onChange={handleChange} />
        ))}
      <TextField
        size="small"
        label="Note for reviewers (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        helperText="e.g. where the specs came from"
      />
      {serverError && <Alert severity="error">{serverError}</Alert>}
      <Box>
        <Button
          variant="contained"
          disableElevation
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Plus size={18} />}
          disabled={submitting}
          onClick={handleSubmit}
          sx={BUTTON_SX}
        >
          Submit for review
        </Button>
      </Box>
    </Stack>
  );
};

const CsvUpload = ({ darkMode, gearType, fields, maxItems, onSubmitted }) => {
  const isMobile = useIsMobile();
  const [rows, setRows] = React.useState([]);
  const [fileName, setFileName] = React.useState("");
  const [warnings, setWarnings] = React.useState([]);
  const [note, setNote] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [serverError, setServerError] = React.useState("");
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    setRows([]);
    setFileName("");
    setWarnings([]);
    setServerError("");
  }, [gearType]);

  const checkedRows = React.useMemo(
    () => rows.map((row) => ({ row, errors: validateSubmissionRow(fields, row) })),
    [rows, fields]
  );
  const validRows = checkedRows.filter(({ errors }) => !errors.length).map(({ row }) => row);
  const invalidCount = checkedRows.length - validRows.length;

  const handleFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;
    setServerError("");
    try {
      const { rows: parsed, unknownColumns, parseErrors } = await parseSubmissionCsv(file, fields);
      const nextWarnings = [...parseErrors];
      if (unknownColumns.length) {
        nextWarnings.push(`Ignored unknown columns: ${unknownColumns.join(", ")}`);
      }
      if (parsed.length > maxItems) {
        nextWarnings.push(`Only the first ${maxItems} rows can be submitted at once`);
      }
      setRows(parsed.slice(0, maxItems));
      setFileName(file.name);
      setWarnings(nextWarnings);
    } catch (error) {
      setServerError("Couldn't read that file. Make sure it's a CSV.");
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setServerError("");
    const result = await submitGear({ gearType, items: validRows, source: "csv", note });
    setSubmitting(false);
    if (!result.ok) {
      setServerError(
        result.rowErrors
          ? `${result.rowErrors.length} rows were rejected: ${result.rowErrors[0].messages.join(", ")}`
          : result.error
      );
      return;
    }
    setRows([]);
    setFileName("");
    setWarnings([]);
    setNote("");
    onSubmitted(result);
  };

  // Preview the identifying columns; errors explain the rest
  const previewKeys = ["brand", "name", "variant", "weight", "price"].filter((key) =>
    fields.some((field) => field.key === key)
  );

  return (
    <Stack gap="24px">
      <Stack direction={isMobile ? "column" : "row"} gap="12px">
        <Button
          variant="outlined"
          startIcon={<Download size={18} />}
          onClick={() => downloadCsvTemplate(gearType, fields)}
          sx={BUTTON_SX}
        >
          Download CSV template
        </Button>
        <Button
          variant="contained"
          disableElevation
          startIcon={<FileUp size={18} />}
          onClick={() => inputRef.current && inputRef.current.click()}
          sx={BUTTON_SX}
        >
          {fileName ? "Upload a different CSV" : "Upload CSV"}
        </Button>
        <input ref={inputRef} type="file" accept=".csv,text/csv" hidden onChange={handleFile} />
      </Stack>
      <Typography sx={{ fontFamily: HEADING_FONT, fontSize: "13px", color: getMutedText(darkMode) }}>
        One item per row, up to {maxItems} at a time. Required columns:{" "}
        {fields.filter((field) => field.required).map((field) => field.key).join(", ")}. Weight is in
        grams; yes/no columns take Yes or No.
      </Typography>
      {warnings.map((warning) => (
        <Alert key={warning} severity="warning">
          {warning}
        </Alert>
      ))}
      {rows.length > 0 && (
        <>
          <Stack direction="row" gap="8px" flexWrap="wrap">
            <Chip color="success" variant="outlined" icon={<CheckCircle2 size={16} />} label={`${validRows.length} ready`} />
            {invalidCount > 0 && (
              <Chip color="error" variant="outlined" icon={<XCircle size={16} />} label={`${invalidCount} need fixes`} />
            )}
          </Stack>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: `1px solid ${getBorder(darkMode)}`, borderRadius: "12px", maxHeight: "420px", backgroundImage: "none" }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  {previewKeys.map((key) => (
                    <TableCell key={key}>{fields.find((field) => field.key === key).label}</TableCell>
                  ))}
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {checkedRows.map(({ row, errors }, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 2}</TableCell>
                    {previewKeys.map((key) => (
                      <TableCell key={key}>{row[key]}</TableCell>
                    ))}
                    <TableCell>
                      {errors.length ? (
                        <Typography sx={{ fontSize: "12px", color: "error.main" }}>{errors.join("; ")}</Typography>
                      ) : (
                        <CheckCircle2 size={16} color="#2e7d32" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TextField
            size="small"
            label="Note for reviewers (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          {serverError && <Alert severity="error">{serverError}</Alert>}
          <Box>
            <Tooltip title={invalidCount ? "Rows that need fixes are skipped; fix them and upload again" : ""}>
              <span>
                <Button
                  variant="contained"
                  disableElevation
                  disabled={!validRows.length || submitting}
                  startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Plus size={18} />}
                  onClick={handleSubmit}
                  sx={BUTTON_SX}
                >
                  Submit {validRows.length} item{validRows.length === 1 ? "" : "s"} for review
                </Button>
              </span>
            </Tooltip>
          </Box>
        </>
      )}
      {!rows.length && serverError && <Alert severity="error">{serverError}</Alert>}
    </Stack>
  );
};

const MySubmissions = ({ darkMode, gearTypeLabels, refreshKey }) => {
  const [submissions, setSubmissions] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      const result = await fetchMySubmissions();
      setSubmissions(result.ok ? result.submissions : []);
    })();
  }, [refreshKey]);

  if (!submissions) return <CircularProgress size={24} />;
  if (!submissions.length) {
    return (
      <Typography sx={{ fontFamily: HEADING_FONT, color: getMutedText(darkMode) }}>
        You haven't submitted any gear yet.
      </Typography>
    );
  }
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Submitted</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {submissions.map((submission) => {
            const label = [submission.data.brand, submission.data.name, submission.data.variant]
              .filter(Boolean)
              .join(" ");
            return (
              <TableRow key={submission._id}>
                <TableCell>
                  {submission.status === "published" && submission.publishedItemId ? (
                    <Link href={`/gear/${GEAR_PAGE_PATH[submission.gearType]}/${submission.publishedItemId}`}>
                      {label}
                    </Link>
                  ) : (
                    label
                  )}
                </TableCell>
                <TableCell>{gearTypeLabels[submission.gearType] || submission.gearType}</TableCell>
                <TableCell>{new Date(submission.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Tooltip title={submission.reviewNote || ""}>
                    <Chip
                      size="small"
                      color={STATUS_COLOR[submission.status]}
                      label={submission.status === "pending" ? "In review" : submission.status}
                      sx={{ textTransform: "capitalize" }}
                    />
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const GearSubmissionContainer = ({ darkMode }) => {
  const isMobile = useIsMobile();
  const [config, setConfig] = React.useState(null);
  const [gearType, setGearType] = React.useState("tents");
  const [tab, setTab] = React.useState("form");
  const [success, setSuccess] = React.useState(null);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const isLoggedIn = getUserIsLoggedIn();

  React.useEffect(() => {
    (async () => {
      const result = await fetchSubmissionFields();
      if (result.ok) setConfig(result);
    })();
  }, []);

  const definition = config && config.gearTypes.find((type) => type.key === gearType);
  const gearTypeLabels = config
    ? Object.fromEntries(config.gearTypes.map((type) => [type.key, type.label]))
    : {};

  const handleSubmitted = (result) => {
    setSuccess(
      `Thanks! ${result.created} item${result.created === 1 ? "" : "s"} submitted for review.` +
        (result.possibleDuplicates
          ? ` ${result.possibleDuplicates} may already be in our catalog; a reviewer will check.`
          : "")
    );
    setRefreshKey((key) => key + 1);
  };

  return (
    <Stack sx={{ width: "100%" }}>
      <Helmet>
        <title>Submit Gear | PackWizard</title>
        <meta
          name="description"
          content="Help grow the PackWizard gear database: submit tents, sleeping pads, sleeping bags, backpacks, insulated jackets and stoves for review."
        />
      </Helmet>
      <GearPageHeader
        darkMode={darkMode}
        eyebrow="Contribute"
        title="Submit Gear"
        subtitle="Know gear we're missing? Add it one at a time or upload a CSV. Every submission is reviewed before it goes live."
      />
      <Box sx={{ width: "100%", maxWidth: "1040px", margin: "0 auto", padding: "0 16px" }}>
        {!isLoggedIn ? (
          <Paper elevation={0} sx={getCardSx(darkMode, isMobile)}>
            <Typography sx={{ fontFamily: HEADING_FONT, fontWeight: 600 }}>
              Log in to submit gear
            </Typography>
            <Typography sx={{ fontFamily: HEADING_FONT, color: getMutedText(darkMode), marginTop: "6px" }}>
              Use the Login button at the top of the page, then come back here. An account lets you
              track the review status of everything you submit.
            </Typography>
          </Paper>
        ) : !config ? (
          <Stack alignItems="center" sx={{ padding: "40px" }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Paper elevation={0} sx={getCardSx(darkMode, isMobile)}>
            <Stack gap="26px">
              <Stack
                direction={isMobile ? "column" : "row"}
                gap="12px"
                justifyContent="space-between"
                alignItems={isMobile ? "stretch" : "center"}
              >
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  fullWidth={isMobile}
                  value={tab}
                  onChange={(e, value) => value && setTab(value)}
                >
                  {[
                    { value: "form", label: "Single item", Icon: Plus },
                    { value: "csv", label: "CSV upload", Icon: FileUp },
                    { value: "mine", label: "My submissions", Icon: ListChecks },
                  ].map(({ value, label, Icon }) => (
                    <ToggleButton
                      key={value}
                      value={value}
                      sx={{
                        ...BUTTON_SX,
                        whiteSpace: "nowrap",
                        padding: isMobile ? "7px 6px" : "6px 12px",
                        fontSize: isMobile ? "13px" : undefined,
                      }}
                    >
                      {!isMobile && <Icon size={16} style={{ marginRight: "6px" }} />}
                      {label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {tab !== "mine" && (
                  <TextField
                    select
                    size="small"
                    label="Gear type"
                    value={gearType}
                    onChange={(e) => setGearType(e.target.value)}
                    sx={{ minWidth: "200px" }}
                  >
                    {config.gearTypes.map((type) => (
                      <MenuItem key={type.key} value={type.key}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
              {success && (
                <Alert severity="success" onClose={() => setSuccess(null)}>
                  {success}{" "}
                  <Link component="button" onClick={() => setTab("mine")} sx={{ verticalAlign: "baseline" }}>
                    View my submissions
                  </Link>
                </Alert>
              )}
              {tab === "form" && definition && (
                <SingleItemForm
                  darkMode={darkMode}
                  gearType={gearType}
                  fields={definition.fields}
                  onSubmitted={handleSubmitted}
                />
              )}
              {tab === "csv" && definition && (
                <CsvUpload
                  darkMode={darkMode}
                  gearType={gearType}
                  fields={definition.fields}
                  maxItems={config.maxItemsPerSubmission}
                  onSubmitted={handleSubmitted}
                />
              )}
              {tab === "mine" && (
                <MySubmissions darkMode={darkMode} gearTypeLabels={gearTypeLabels} refreshKey={refreshKey} />
              )}
            </Stack>
          </Paper>
        )}
      </Box>
    </Stack>
  );
};

export default GearSubmissionContainer;
